import React, { useState, useEffect } from 'react';
import { Airport, CreateAirportRequest, UpdateAirportRequest } from '../services/airportService';
import './AirportModal.css';

interface AirportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  airport?: Airport | null;
  isEdit?: boolean;
}

const AirportModal: React.FC<AirportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  airport,
  isEdit = false
}) => {
  const [formData, setFormData] = useState<{
    code: string;
    name: string;
    displayName: string;
    isActive: boolean;
  }>({
    code: '',
    name: '',
    displayName: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (airport && isEdit) {
      setFormData({
        code: airport.code,
        name: airport.name,
        displayName: airport.displayName,
        isActive: airport.isActive
      });
    } else {
      setFormData({
        code: '',
        name: '',
        displayName: '',
        isActive: true
      });
    }
    setError(null);
  }, [airport, isEdit]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.code.trim()) {
      setError('Код аэропорта обязателен');
      return false;
    }

    if (formData.code.length < 2 || formData.code.length > 10) {
      setError('Код должен содержать от 2 до 10 символов');
      return false;
    }

    if (!formData.name.trim()) {
      setError('Название аэропорта обязательно');
      return false;
    }

    if (formData.name.length < 2 || formData.name.length > 100) {
      setError('Название должно содержать от 2 до 100 символов');
      return false;
    }

    if (!formData.displayName.trim()) {
      setError('Отображаемое название обязательно');
      return false;
    }

    if (formData.displayName.length < 2 || formData.displayName.length > 100) {
      setError('Отображаемое название должно содержать от 2 до 100 символов');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEdit && airport) {
        const updateData: UpdateAirportRequest = {
          code: formData.code,
          name: formData.name,
          displayName: formData.displayName,
          isActive: formData.isActive
        };
        await import('../services/airportService').then(module => 
          module.default.updateAirport(airport.id, updateData)
        );
      } else {
        const createData: CreateAirportRequest = {
          code: formData.code,
          name: formData.name,
          displayName: formData.displayName,
          isActive: formData.isActive
        };
        await import('../services/airportService').then(module => 
          module.default.createAirport(createData)
        );
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error saving airport:', err);
      setError(err.message || 'Ошибка при сохранении аэропорта');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="airport-modal-overlay" onClick={onClose}>
      <div className="airport-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="airport-modal-header">
          <h2>{isEdit ? 'Редактировать аэропорт' : 'Создать аэропорт'}</h2>
          <button className="airport-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="airport-modal-form">
          <div className="form-group">
            <label htmlFor="code">Код аэропорта *</label>
            <input
              type="text"
              id="code"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="ASTANA"
              required
              minLength={2}
              maxLength={10}
              disabled={isEdit}
            />
            <small>Уникальный код аэропорта (2-10 символов)</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">Название аэропорта *</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Астана"
              required
              minLength={2}
              maxLength={100}
            />
            <small>Полное название аэропорта</small>
          </div>

          <div className="form-group">
            <label htmlFor="displayName">Отображаемое название *</label>
            <input
              type="text"
              id="displayName"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              placeholder="Астана"
              required
              minLength={2}
              maxLength={100}
            />
            <small>Название для отображения в интерфейсе</small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              Активен
            </label>
            <small>Активные аэропорты доступны для использования</small>
          </div>

          {error && (
            <div className="airport-modal-error">
              <p>{error}</p>
            </div>
          )}

          <div className="airport-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="airport-modal-button secondary"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="airport-modal-button primary"
              disabled={loading}
            >
              {loading ? 'Сохранение...' : (isEdit ? 'Обновить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AirportModal;
